import { hasValue, isNil } from 'swipeclock-frontend';
import { AdminRoles, SuperAdminRoles } from 'data/masterdata';
import {
  hasPermission,
  meetsMinimumPermissionLevel,
} from 'helpers/permissionsHelper';
import { feature as FeatureEnum, hasFeatureInFeatures } from 'enums/feature';
import { permissions as PermissionsEnum } from 'enums/permissions';
import { PERMISSION_NOT_GRANTED } from 'data/constants';

export default function rulesEvaluator(
  features,
  roles,
  permissions,
  permissionsDictionary = {}
) {
  // Helper Methods for rules

  // featureIds:
  // It's a dictionary: key - featureCategoryId, value - count
  const featureIds = features?.reduce((ids, feature) => {
    const key = feature?.id || 0;
    const count = hasValue(ids[key]) ? ids[key] + 1 : 1;
    return { ...ids, [key]: count };
  }, {});

  // categoryIds:
  // It's a dictionary: key - featureCategoryId, value - count
  const featureCategoryIds = features?.reduce((ids, feature) => {
    const key = feature?.featureCategoryId || 0;
    const count = hasValue(ids[key]) ? ids[key] + 1 : 1;
    return { ...ids, [key]: count };
  }, {});

  const isAdmin = () => {
    return AdminRoles?.some((adminRole) => {
      const index = roles?.findIndex((x) => x?.roleType === adminRole);
      return index > -1;
    });
  };

  const userHasRole = (r) =>
    r?.some((role) => {
      return roles?.findIndex((x) => x.roleType === role) > -1;
    });

  const hasSomeFeatureCategoryIds = (ids) =>
    ids?.some((id) => featureCategoryIds?.[id]);

  const hasSomeFeatureIds = (ids) => !!ids?.some((id) => featureIds?.[id]);

  const hasSomeExcludedFeatureIds = (ids) => {
    if (isNil(ids)) {
      return false;
    }

    return !hasSomeFeatureIds(ids);
  };

  const hasAllowedRole = (itemRoles) => {
    //no roles assigned to the drawer item, all have permission.
    if (!hasValue(itemRoles)) {
      return true;
    }

    // If either SuperAdmin or Partner are present in the itemRoles array,
    // verify that the user has the specific permission required
    if (SuperAdminRoles.some((role) => itemRoles.includes(role))) {
      return userHasRole(itemRoles);
    }
    // Otherwise give permission as long as the user has any Admin role
    if (isAdmin()) {
      return true;
    }

    //Lastly, check the roles we have against the user roles
    return userHasRole(itemRoles);
  };

  const meetsPermissionRequirement = (requirement) => {
    if (isNil(requirement)) {
      return true;
    }

    const level =
      permissionsDictionary[requirement.type] ?? PERMISSION_NOT_GRANTED;
    return meetsMinimumPermissionLevel(level, requirement.minimumLevel);
  };

  // Public methods to run rules an evaluate

  // Method to evaluate drawer items
  function checkIfDrawerItemIncluded(item) {
    const {
      featureIds: itemIds,
      excludedFeatureIds,
      roles: itemRoles,
    } = item ?? {};

    // We check this first
    // It doesn't work in the rules reduce because
    // this could fail but they still have features so it would show
    // If something is based on admin roles it trumps everything else
    if (!hasAllowedRole(itemRoles)) {
      return false;
    }

    // Special case for org chart
    // We want to check to see if user has permission to view org chart
    // Note: if we add more special cases we should refactor this
    if (itemIds?.includes(FeatureEnum.organizationChart) && hasAllowedRole) {
      const hasOrganizationChartPermission = hasPermission({
        features,
        permissions,
        permissionId: PermissionsEnum.viewOrgChart,
        roles,
      });

      const hasOrganizationChartFeature = hasFeatureInFeatures(
        features,
        FeatureEnum.organizationChart,
        roles
      );

      const canViewOrgChart =
        hasOrganizationChartFeature && hasOrganizationChartPermission;

      // Specifically checking for false because if canViewOrgChart is truthy,
      // we want to continue down the rules to see if there are any other
      // things that would exclude this item. Therefore not doing something
      // elegant like return !canViewOrgChart
      if (!canViewOrgChart) {
        return false;
      }
    }

    // Setup helper booleans for rules
    const hasExcludedFeatureId = hasSomeExcludedFeatureIds(excludedFeatureIds);
    const hasFeatureId = hasSomeFeatureIds(itemIds);

    const included = isNil(itemIds) && isNil(excludedFeatureIds);

    // Assemble rules array with bools
    const rules = [hasExcludedFeatureId, hasFeatureId, included];

    return rules.reduce((evaluation, rule) => {
      return evaluation || rule;
    }, false);
  }

  function checkIfTopAppBarItemIncluded(item) {
    const {
      featureCategoryIds: itemIds,
      roles: itemRoles,
      featureIds: itemFeatureIds,
      permissionRequirement,
    } = item ?? {};
    const hasFeatureCategoryId =
      hasSomeFeatureCategoryIds(itemIds) || !hasValue(itemIds);
    const hasFeatureIds =
      hasSomeFeatureIds(itemFeatureIds) || !hasValue(itemFeatureIds);
    return (
      hasFeatureCategoryId &&
      hasFeatureIds &&
      hasAllowedRole(itemRoles) &&
      meetsPermissionRequirement(permissionRequirement)
    );
  }

  return {
    checkIfDrawerItemIncluded,
    checkIfTopAppBarItemIncluded,
  };
}
